import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { preserveMathCursorPosition } from "@/components/tiptap/utils/preserveMathCursorPosition";

import { appendBlock } from "../create-blocks/utils/appendBlock";
import { getCollapsedButtonProps } from "../getCollapsedButtonProps";

import type { DocumentModel } from "@/models/DocumentModel";

type Props = {
  doc: DocumentModel;
  isCollapsed?: boolean;
  disabled?: boolean;
};

export const ChecklistButton = observer(function ChecklistButton({
  doc,
  isCollapsed,
  disabled,
}: Props) {
  const t = useTranslations("sidebar.createBlocks");
  const { tiptap, editorState } = doc.editor;

  if (!tiptap) {
    return null;
  }

  const handleOnClick = () => {
    if (editorState.isNonConvertableBlock) {
      appendBlock(doc, "taskList");
      return;
    }

    preserveMathCursorPosition(doc, () => {
      let chain = tiptap.chain().unsetCallout();
      // If in blockquote, convert it to taskList
      if (editorState.isBlockquote) {
        chain = chain.toggleBlockquote();
      }
      chain.toggleTaskList().run();
    });
  };

  return (
    <Sidebar.Button
      data-testid="task-list-block-type"
      icon={<Icon name="block-format/Task" />}
      title={t("taskList.title")}
      onClick={handleOnClick}
      isActive={editorState.isTaskList}
      aria-pressed={editorState.isTaskList}
      disabled={disabled}
      inverted
      {...getCollapsedButtonProps(isCollapsed)}
    />
  );
});
